import { useCallback, useEffect, useReducer, useRef } from 'react';

import { limit, onSnapshot, query, startAfter } from 'firebase/firestore';

import getDocData from '../../firebase/getDocData';
import {
  realtimePaginationFetchBatchSuccess,
  realtimePaginationFirstFetch,
  realtimePaginationFirstFetchError,
  realtimePaginationNextFetch,
  realtimePaginationNextFetchError,
  realtimePaginationReset,
} from './actions';
import { getInitialState, reducer } from './reducer';

const DEFAULT_PAGE_SIZE = 10;

const parseSnapshot = (snapshot, pageSize) => {
  const lastDoc = snapshot.docs[snapshot.size - 1];
  const docs = {};
  snapshot.docs.forEach(item => {
    const doc = getDocData(item);
    docs[doc.id] = doc;
  });
  return { lastDoc, docs, hasMore: snapshot.size === pageSize };
};

// This hook helps to subscribe to data in your Firestore database in multiple queries without having to worry
// about state management and pagination. Instead of calling Firestore's onSnapshot(query(...), () => {}) in a loop
// you simply pass a query list to useFirestoreRealtimeBatchedPagination() and you get back everything you need,
// including loaded, loading, data, and error.
// Your component will re-render when data changes and your subscription will be
// automatically removed when the component unmounts.
// use it like so:
// const { data, error, loading, loaded, next } = useFirestoreRealtimeBatchedPagination(queryRef[], dependencies, pageSize);
// where next is a callback action that you can trigger when you need the next page

// ### YOU MIGHT NEED AN INDEX TO USE THIS ON PRODUCTION ! ###
const useFirestoreRealtimeBatchedPagination = (
  queryRefList,
  dependencies,
  pageSize = DEFAULT_PAGE_SIZE,
) => {
  const [state, dispatch] = useReducer(reducer, getInitialState());
  const stateRef = useRef();
  const baseQueryByBatchRef = useRef({});
  const unsubscribesRef = useRef([]);
  stateRef.current = state;
  const loading = Object.values(stateRef.current.loadingByBatch).some(i => i);
  const loaded = Object.values(stateRef.current.loadedByBatch).every(i => i);

  const unsubscribe = () => {
    unsubscribesRef.current.forEach(unsub => {
      unsub();
    });
    unsubscribesRef.current = [];
  };

  const onNextFetchError = useCallback(
    error => dispatch(realtimePaginationNextFetchError(error)),
    [dispatch],
  );
  const onNextFetchBatchSuccess = useCallback(
    (batchId, lastDocument, data, hasMore) =>
      dispatch(realtimePaginationFetchBatchSuccess(batchId, lastDocument, data, undefined, hasMore)),
    [dispatch],
  );

  const getNextFetch = useCallback(() => {
    if (loading) {
      return;
    }

    const batchesWithMore = Object.keys(stateRef.current.gotNewDataByBatch).filter(
      batchId =>
        stateRef.current.gotNewDataByBatch[batchId] && stateRef.current.lastDocumentByBatch[batchId],
    );
    if (!batchesWithMore.length) {
      return;
    }

    dispatch(realtimePaginationNextFetch(batchesWithMore));
    batchesWithMore.forEach(batchId => {
      const currentLastDoc = stateRef.current.lastDocumentByBatch[batchId];
      const baseQuery = baseQueryByBatchRef.current[batchId];
      const newQuery = query(baseQuery, startAfter(currentLastDoc), limit(pageSize));

      const unsub = onSnapshot(
        newQuery,
        snapshot => {
          const { lastDoc, docs, hasMore } = parseSnapshot(snapshot, pageSize);
          onNextFetchBatchSuccess(batchId, lastDoc, docs, hasMore);
        },
        onNextFetchError,
      );
      unsubscribesRef.current.push(unsub);
    });
  }, [loading, pageSize, onNextFetchBatchSuccess, onNextFetchError]);

  const onFirstFetchError = useCallback(
    error => dispatch(realtimePaginationFirstFetchError(error)),
    [dispatch],
  );
  const onFirstFetchBatchSuccess = useCallback(
    (batchId, lastDocument, data, hasMore) =>
      dispatch(
        realtimePaginationFetchBatchSuccess(batchId, lastDocument, data, getNextFetch, hasMore),
      ),
    [dispatch, getNextFetch],
  );

  const getFirstFetch = useCallback(() => {
    dispatch(realtimePaginationFirstFetch(queryRefList.length));

    baseQueryByBatchRef.current = {};
    queryRefList.forEach((currentQry, idx) => {
      baseQueryByBatchRef.current[idx] = currentQry;
      const newQuery = query(currentQry, limit(pageSize));

      const unsub = onSnapshot(
        newQuery,
        snapshot => {
          const { lastDoc, docs, hasMore } = parseSnapshot(snapshot, pageSize);
          onFirstFetchBatchSuccess(idx, lastDoc, docs, hasMore);
        },
        onFirstFetchError,
      );
      unsubscribesRef.current.push(unsub);
    });
  }, [dispatch, onFirstFetchError, onFirstFetchBatchSuccess, pageSize, queryRefList]);

  useEffect(() => {
    // If the dependencies changes, then reset
    unsubscribe();
    dispatch(realtimePaginationReset());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  useEffect(() => {
    if (!queryRefList.length) {
      return;
    }
    if (!loaded && !loading) {
      getFirstFetch();
    }
  }, [dispatch, getFirstFetch, loaded, loading, queryRefList]);

  // Unmount Effect
  useEffect(() => unsubscribe, []);

  const data = [];
  const currentState = stateRef.current;
  Object.values(currentState.dataByBatch).map(batch =>
    Object.values(batch).map(item => data.push(item)),
  );
  const shortState = {
    data,
    error: currentState.error,
    gotNewData: Object.values(currentState.gotNewDataByBatch).some(i => i),
    loaded: Object.values(currentState.loadedByBatch).every(i => i),
    loading: Object.values(currentState.loadingByBatch).some(i => i),
    next: currentState.next,
  };

  return shortState;
};

export default useFirestoreRealtimeBatchedPagination;
